import { Box } from '@chakra-ui/react'
import { FC, useEffect, useMemo, useState } from 'react'
import { getLocale } from '../../i18n/locale'
import { getCookie } from '../../scenarios/storage'
import { ScenarioType } from '../../state/interfaces'
import { isTheDomainAWhitelabel } from '../../whitelabels/whitelabel-domains'

const GPT_SCRIPT_ID = 'question-google-publisher-tag'
const GPT_SCRIPT_URL = 'https://www.googletagservices.com/tag/js/gpt.js'
const BANNER_SLOT_ID = 'email-go-page-horizontal-banner'
const TABLET_BREAKPOINT_QUERY = '(min-width: 738px)'
const AD_MANAGER_CODE = '80203491'
const RED_FLAG_DEALS_AD_MANAGER_CODE = '1030735'

let gptScriptPromise: Promise<void> | null = null

type EmailGoPageAdBannerProps = {
  scenarioType: ScenarioType.EMAIL_PAGE_GO_INTERNET | ScenarioType.EMAIL_PAGE_GO_MOBILE
}

declare global {
  interface Window {
    googletag?: any
  }
}

const ensureGoogleTag = () => {
  window.googletag = window.googletag || {}
  window.googletag.cmd = window.googletag.cmd || []
  return window.googletag
}

const loadGooglePublisherTag = () => {
  ensureGoogleTag()

  if (document.getElementById(GPT_SCRIPT_ID)) {
    return gptScriptPromise || Promise.resolve()
  }

  gptScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = GPT_SCRIPT_ID
    script.src = GPT_SCRIPT_URL
    script.async = true
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener('error', () => reject(new Error('Failed to load Google Publisher Tag')), { once: true })
    document.head.appendChild(script)
  })

  return gptScriptPromise
}

const getBannerTargetingKeyValuesFromCookie = (): Record<string, string> => {
  const targetingCookie = getCookie('adBannerTargetingKeyValues')

  if (!targetingCookie) {
    return {}
  }

  try {
    return JSON.parse(targetingCookie)
  } catch (_error) {
    return {}
  }
}

const shouldSkipGooglePublisherTag = () =>
  window.location.hostname.indexOf('localhost') === 0 || window.location.hostname.indexOf('127.0.0') === 0

export const isEmailGoPageAdBannerAllowedForHostname = (hostname: string) => {
  const normalizedHostname = hostname.toLowerCase()
  const isPlanHubHostname = normalizedHostname === 'planhub.ca' || normalizedHostname.endsWith('.planhub.ca')

  return isPlanHubHostname && !isTheDomainAWhitelabel(normalizedHostname)
}

const getAdManagerCode = () =>
  window.location.hostname.includes('redflagdeals') ? RED_FLAG_DEALS_AD_MANAGER_CODE : AD_MANAGER_CODE

const getSourceTargeting = () => {
  const hostname = window.location.hostname

  if (hostname.includes('mobilesyrup.planhub.ca')) {
    return 'mobilesyrup'
  }

  if (hostname.includes('ratehub.planhub.ca')) {
    return 'ratehub'
  }

  if (hostname.includes('francoischarron.planhub.ca')) {
    return 'francoischarron'
  }

  if (hostname.includes('protegezvous.planhub.ca')) {
    return 'protegezvous'
  }

  if (hostname.includes('lactualite.planhub.ca')) {
    return 'lactualite'
  }

  if (hostname.includes('redflagdeals.planhub.ca')) {
    return 'redflagdeals'
  }

  return 'default'
}

const getAdUnitCode = (scenarioType: EmailGoPageAdBannerProps['scenarioType']) =>
  scenarioType === ScenarioType.EMAIL_PAGE_GO_INTERNET ? 'internet' : 'mobile'

const getBannerConfig = (scenarioType: EmailGoPageAdBannerProps['scenarioType']) => {
  const isTabletOrLarger = typeof window.matchMedia === 'function' && window.matchMedia(TABLET_BREAKPOINT_QUERY).matches
  const locale = getLocale()
  const device = isTabletOrLarger ? 'desktop' : 'mobile'
  const sizes = isTabletOrLarger ? [[728, 90]] : [[320, 50]]

  return {
    adUnitPath: `/${getAdManagerCode()}/planhub_${locale}_${device}/${getAdUnitCode(scenarioType)}`,
    nav: getAdUnitCode(scenarioType),
    sizes,
  }
}

export const EmailGoPageAdBanner: FC<EmailGoPageAdBannerProps> = ({ scenarioType }) => {
  const bannerConfig = useMemo(() => getBannerConfig(scenarioType), [scenarioType])
  const [isBannerVisible, setIsBannerVisible] = useState(false)

  useEffect(() => {
    setIsBannerVisible(false)

    if (shouldSkipGooglePublisherTag()) {
      return undefined
    }

    let isCancelled = false
    let slot: any = null
    let pubadsService: any = null
    let handleSlotRenderEnded: ((event: any) => void) | undefined

    loadGooglePublisherTag()
      .then(() => {
        const googletag = ensureGoogleTag()

        googletag.cmd.push(() => {
          if (isCancelled) {
            return
          }

          const existingSlot = googletag
            .pubads()
            .getSlots()
            .find((currentSlot: any) => currentSlot.getSlotElementId() === BANNER_SLOT_ID)

          slot =
            existingSlot ||
            googletag
              .defineSlot(bannerConfig.adUnitPath, bannerConfig.sizes, BANNER_SLOT_ID)
              ?.setTargeting('position', '1')
              .addService(googletag.pubads())

          if (!slot) {
            return
          }

          pubadsService = googletag.pubads()
          handleSlotRenderEnded = (event: any) => {
            if (!isCancelled && event.slot.getSlotElementId() === BANNER_SLOT_ID) {
              setIsBannerVisible(!event.isEmpty)
            }
          }

          pubadsService.addEventListener('slotRenderEnded', handleSlotRenderEnded)
          pubadsService.setTargeting('source', getSourceTargeting())
          pubadsService.setTargeting('nav', bannerConfig.nav)
          pubadsService.setTargeting('locale', getLocale())
          pubadsService.setTargeting('phprovince', getCookie('province') || 'not set')

          const bannerTargetingKeyValues = getBannerTargetingKeyValuesFromCookie()
          Object.keys(bannerTargetingKeyValues).forEach((key) => {
            pubadsService.setTargeting(key, bannerTargetingKeyValues[key])
          })

          pubadsService.collapseEmptyDivs()
          googletag.enableServices()
          googletag.display(BANNER_SLOT_ID)
        })
      })
      .catch((_error) => {
        console.warn('Failed to load Google Publisher Tag')
      })

    return () => {
      isCancelled = true

      if (pubadsService?.removeEventListener && handleSlotRenderEnded) {
        pubadsService.removeEventListener('slotRenderEnded', handleSlotRenderEnded)
      }

      if (slot && window.googletag?.destroySlots) {
        window.googletag.cmd.push(() => window.googletag.destroySlots([slot]))
      }
    }
  }, [bannerConfig])

  return (
    <Box
      as="section"
      aria-label="Advertisement"
      data-testid="email-go-page-ad-banner"
      display={isBannerVisible ? 'flex' : 'block'}
      justifyContent="center"
      height={isBannerVisible ? 'auto' : 0}
      marginTop={isBannerVisible ? { base: 4, md: 6 } : 0}
      marginBottom={isBannerVisible ? { base: 2, md: 4 } : 0}
      overflow={isBannerVisible ? 'visible' : 'hidden'}
      paddingX={2}
      width="100%"
    >
      <Box
        id={BANNER_SLOT_ID}
        className="targetableBanner"
        minHeight={isBannerVisible ? { base: '50px', md: '90px' } : 0}
        maxWidth="100%"
        width={{ base: '320px', md: '728px' }}
      />
    </Box>
  )
}
